/**
 * TanStack Query hooks for Course resource.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

export type CefrLevel = 'pre_a1' | 'a1' | 'a2' | 'b1' | 'b2' | 'c1' | 'c2';

export interface Course {
  id: string;
  code: string;
  name: string;
  level: CefrLevel;
  description?: string | null;
  totalSessions: number;
  sessionDurationMin?: number | null;
  targetAgeMin?: number | null;
  targetAgeMax?: number | null;
  isActive: boolean;
  entryCriteria?: string | null;
  exitCriteria?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListCoursesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  level?: CefrLevel;
  isActive?: boolean;
}

export const courseKeys = {
  all: ['courses'] as const,
  list: (params?: ListCoursesParams) => ['courses', 'list', params] as const,
  detail: (id: string) => ['courses', 'detail', id] as const,
};

export function useCourses(params?: ListCoursesParams) {
  return useQuery({
    queryKey: courseKeys.list(params),
    queryFn: () =>
      api.getPage<Course>('/api/courses', params as Record<string, string>),
  });
}

export function useCourse(id: string) {
  return useQuery({
    queryKey: courseKeys.detail(id),
    queryFn: () => api.get<Course>(`/api/courses/${id}`),
    enabled: !!id,
  });
}

export function useCreateCourse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.post<Course>('/api/courses', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: courseKeys.all });
    },
  });
}

export function useUpdateCourse(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => api.patch<Course>(`/api/courses/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: courseKeys.all });
    },
  });
}
